import React, { useCallback, useEffect, useState } from 'react';
import {
  clearSavedMeasurements,
  deleteSavedMeasurement,
  downloadMeasurementsCsv,
  measureTag,
  measureColor,
  readSavedMeasurements,
} from '../../Utils/savedMeasurements';
import { getOrigin } from '../../config/siteConfig';
import { useClickStore } from '../../store/clickStore';
import { useShallow } from 'zustand/react/shallow';

export default function MeasurementList({ layout = 'row' }) {
  const [list, setList] = useState(() => readSavedMeasurements());
  const { selectedMeasureId, setSelectedMeasureId } = useClickStore(
    useShallow((s) => ({
      selectedMeasureId: s.selectedMeasureId,
      setSelectedMeasureId: s.setSelectedMeasureId,
    }))
  );

  const refresh = useCallback(() => {
    setList(readSavedMeasurements());
  }, []);

  useEffect(() => {
    const onChange = () => refresh();
    window.addEventListener('mdv-measurements-changed', onChange);
    return () => window.removeEventListener('mdv-measurements-changed', onChange);
  }, [refresh]);

  const onExport = () => {
    downloadMeasurementsCsv(list, getOrigin());
  };

  if (!list.length) {
    return (
      <span className="text-[10px] text-zinc-500 px-1">
        Click two points with Distance (D). Lines stay until you delete them.
      </span>
    );
  }

  return (
    <div className={layout === 'column' ? 'flex flex-col gap-1 w-full' : 'flex items-center gap-1 min-w-0'}>
      <div className={layout === 'column' ? 'flex flex-col gap-0.5 max-h-40 overflow-y-auto' : 'flex items-center gap-1 overflow-x-auto min-w-0'}>
        {list.map((m) => {
          const active = m.id === selectedMeasureId;
          const tag = measureTag(m);
          const color = measureColor(m);
          const title = `${tag} · Distance ${m.dist} m · Azimuth ${m.a}° · Dip ${m.d}°`;
          return (
            <span
              key={m.id}
              className="relative inline-flex items-center gap-0.5 shrink-0 overflow-hidden rounded-sm border bg-zinc-950"
              style={{
                borderColor: active ? color : `${color}55`,
                backgroundColor: active ? `${color}22` : '#09090b',
              }}
            >
              <button
                type="button"
                title={title}
                className={`m-0 px-2 text-left text-[10px] tabular-nums cursor-pointer ${
                  layout === 'column' ? 'h-auto py-1 pb-1.5' : 'h-7'
                } text-zinc-200`}
                onClick={() => setSelectedMeasureId(selectedMeasureId === m.id ? null : m.id)}
              >
                {layout === 'column' ? (
                  <span className="flex flex-col leading-tight">
                    <span className="font-semibold">{tag}  {m.dist} m</span>
                    <span className="text-[9px] text-zinc-400">Az {m.a}°  Dip {m.d}°</span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="font-semibold">{tag}</span>
                    <span>
                      {m.dist}m  Az {m.a}°  Dip {m.d}°
                    </span>
                  </span>
                )}
              </button>
              <button
                type="button"
                className="m-0 h-7 w-6 text-zinc-500 hover:text-red-400"
                title="Delete measurement"
                onClick={() => {
                  if (selectedMeasureId === m.id) setSelectedMeasureId(null);
                  setList(deleteSavedMeasurement(m.id));
                }}
              >
                ×
              </button>
              <span
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px]"
                style={{ backgroundColor: color }}
              />
            </span>
          );
        })}
      </div>
      <button
        type="button"
        className="m-0 h-7 px-2 shrink-0 rounded-sm border border-yellow-600/40 text-[10px] uppercase tracking-wider text-yellow-400 hover:bg-yellow-500/10"
        onClick={onExport}
      >
        CSV
      </button>
      <button
        type="button"
        className="m-0 h-7 px-2 shrink-0 rounded-sm border border-zinc-700 text-[10px] uppercase tracking-wider text-zinc-400 hover:text-red-400"
        onClick={() => {
          setSelectedMeasureId(null);
          clearSavedMeasurements();
          refresh();
        }}
      >
        Clear
      </button>
    </div>
  );
}
